import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Grid, Typography, Button, CircularProgress } from "@mui/material";
import LabelButton from "../../components/reusable-components/buttons/LabelButton";
import InnoTextInput from "../../components/inputs/InnoTextInput";
import { useValidation } from "../../helpers/validationHelpers";
import { useTrack } from "../../analytics/hooks";
import BotDetection from "./BotDetection";

const commonTypographyStyles = {
  fontFamily: "Lato",
  fontStyle: "normal",
  fontWeight: 700,
  lineHeight: "normal",
};

const primaryTypographyStyles = {
  ...commonTypographyStyles,
  color: "var(--Primary-500, #16124C)",
};

const secondaryTypographyStyles = {
  ...commonTypographyStyles,
  color: "var(--Secondary-500, #AC5CCC)",
};

export default function CustomLoginPage() {
  const navigage = useNavigate();
  const track = useTrack();
  const [directToGoogleLogin, setDirectToGoogleLogin] = useState(false);
  const [googleSubmitInProcess, setGoogleSubmitInProcess] = useState(false);
  const [userNotInDB, setUserNotInDB] = useState(false);
  const [userNotFinishRegistration, setUserNotFinishRegistration] =
    useState(false);
  const [email, setEmail] = useState("");
  const [userForTrack, setUserForTrack] = useState({});
  const { errors, validate } = useValidation({
    email: {
      isValidEmail: true,
    },
  });
  const [errorFromAPI, setErrorFromAPI] = useState(null);
  const [botCheckPassed, setBotCheckPassed] = useState();

  const handleEmailInput = (val) => {
    //clean the email error in errors
    if (errors?.email?.isValid === false) {
      errors.email.isValid = true;
      errors.email.errorMessages = {};
    }
    setUserNotInDB(false);
    setUserNotFinishRegistration(false);
    setErrorFromAPI(null);
    setEmail(val);
  };

  const handleContinue = async () => {
    //first check if the email is valid
    validate("email", email);

    if (errors?.email?.isValid === false) {
      return;
    }

    //we need to check user status in our database
    const res = await fetch("/auth/check-login-email", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email: email }),
    });
    if (res.ok) {
      const data = await res.json();

      setUserForTrack({
        id: data.userId,
        email: email,
        isSuperAdmin: data.isSuperAdmin,
      });

      track("user_login_attempt_initiated", {
        user: {
          id: data.userId,
          email: email,
          isSuperAdmin: data.isSuperAdmin,
        },
        flow_type: "LOGIN",
      });

      if (!data.userInDB) {
        //user not in db
        setUserNotInDB(true);
      } else {
        setUserNotInDB(false);
        if (data.registered) {
          if (data.googleLogin) {
            //track the event for google log in
            track("user_login_auth_method_presented", {
              user: {
                id: data.userId,
                email: email,
                isSuperAdmin: data.isSuperAdmin,
              },
              auth_method_presented: "GOOGLE",
            });
            setDirectToGoogleLogin(true);
          } else {
            //track the event for email log in
            track("user_login_auth_method_presented", {
              user: {
                id: data.userId,
                email: email,
                isSuperAdmin: data.isSuperAdmin,
              },
              auth_method_presented: "PASSWORD",
            });

            // Delay the redirect to give Segment time to send the event
            setTimeout(() => {
              window.location.href = `${
                process.env.REACT_APP_URL
              }/auth/email-login?email=${encodeURIComponent(email)}`;
            }, 300); // 300ms is usually enough, but can adjust to 500ms if needed
          }
        } else {
          setUserNotFinishRegistration(true);
        }
        return;
      }
    } else {
      //track the event of user_login_attempt_initiated
      track("user_login_attempt_initiated", {
        user: { id: undefined, email: email, isSuperAdmin: false },
        flow_type: "LOGIN",
      });
      const data = await res.json();
      setErrorFromAPI(data.error);
      console.error("Failed to check email in db");
    }
  };

  const handleSwitchToRegister = () => {
    navigage("/register", { replace: true });
  };

  const handleGoogleSubmit = async () => {
    //track the login with google event
    track("user_authentication_initiated", {
      user: userForTrack,
      auth_method: "GOOGLE",
      flow_type: "LOGIN",
    });
    setGoogleSubmitInProcess(true);

    // Delay the redirect to give Segment time to send the event
    setTimeout(() => {
      window.location.href = `${process.env.REACT_APP_URL}/auth/google-login`;
    }, 300); // 300ms is usually enough, but can adjust to 500ms if needed
  };

  return (
    <Grid
      item
      sx={{
        width: "100%",
        height: "100%",
        backgroundColor: "#FFFFFF",
        padding: "20px",
      }}>
      <Grid container sx={{ height: "100%", width: "100%" }}>
        <Grid item xs={12} md={6} sx={{ height: "100%" }}>
          <Grid
            container
            direction="column"
            sx={{
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
              height: "100%",
              overflowY: "auto",
            }}>
            <Grid
              item
              sx={{
                height: "auto",
                width: "350px",
                padding: "87px 0 87px 0",
              }}>
              <Grid
                container
                direction="column"
                sx={{
                  width: "100%",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: "20px",
                }}>
                {/* main Title */}
                <Grid
                  item
                  sx={{
                    width: "100%",
                    height: "30px",
                    marginBottom: "24px",
                  }}>
                  <Typography
                    sx={{
                      ...primaryTypographyStyles,
                      fontSize: 25,
                      fontWeight: 900,
                    }}>
                    {directToGoogleLogin ? (
                      "Log in with Google"
                    ) : (
                      <>
                        Login to your{" "}
                        <span style={{ ...secondaryTypographyStyles }}>
                          school
                        </span>
                      </>
                    )}
                  </Typography>
                </Grid>
                {/* form area */}
                <Grid
                  item
                  sx={{
                    width: "100%",
                    height: "auto",
                  }}>
                  <Grid
                    container
                    direction="column"
                    sx={{
                      width: "100%",
                      height: "auto",
                      justifyContent: "space-between",
                    }}>
                    {/* email */}
                    {!directToGoogleLogin && (
                      <Grid
                        item
                        sx={{
                          width: "100%",
                          height: "75px",
                        }}>
                        <Typography
                          sx={{
                            color: "#A4A4A4",
                            fontFamily: "Lato",
                            fontSize: 14,
                            fontStyle: "normal",
                            fontWeight: 400,
                            lineHeight: "normal",
                            marginBottom: "10px",
                          }}>
                          Email
                        </Typography>
                        <Grid
                          item
                          sx={{
                            width: "100%",
                            height: "40px",
                          }}>
                          <InnoTextInput
                            fieldName="email"
                            placeholder="Your email"
                            fieldValue={email}
                            setValue={handleEmailInput}
                            width="100%"
                            height="40px"
                            styledProps={{
                              fontFamily: "Lato",
                              fontSize: "14px",
                              fontWeight: 700,
                              fontStyle: "normal",
                              color: "var(--Primary-900, #0B0926)",
                            }}
                            placeholderStyles={{
                              fontFamily: "Lato",
                              fontSize: "14px",
                              fontWeight: 700,
                              fontStyle: "normal",
                              color: "var(--Neutrals-700, #C6CCD2)",
                            }}
                            errorIcon={false}
                            errors={errors}
                            showErrorIconWithError={false}
                            feedbackError={userNotInDB}
                          />
                        </Grid>
                        {userNotInDB || userNotFinishRegistration ? (
                          <Typography
                            sx={{
                              color: "var(--Errors-500, #C30930)",
                              fontFamily: "Lato",
                              fontSize: 12,
                              fontStyle: "normal",
                              fontWeight: 700,
                              lineHeight: "normal",
                              paddingLeft: "5px",
                              marginTop: "5px",
                            }}>
                            {userNotInDB
                              ? "Email not recognized. "
                              : "Your registration is incomplete. "}
                            {"Please "}
                            <span
                              style={{
                                color: "var(--Tertiary-500, #00A6FF)",
                                cursor: "pointer",
                              }}
                              onClick={handleSwitchToRegister}>
                              {userNotInDB
                                ? "register here"
                                : "complete registration"}
                            </span>
                          </Typography>
                        ) : (
                          errorFromAPI && (
                            <Typography
                              sx={{
                                color: "var(--Errors-500, #C30930)",
                                fontFamily: "Lato",
                                fontSize: 12,
                                fontStyle: "normal",
                                fontWeight: 700,
                                lineHeight: "normal",
                                paddingLeft: "5px",
                                marginTop: "5px",
                              }}>
                              {errorFromAPI}
                            </Typography>
                          )
                        )}
                      </Grid>
                    )}
                  </Grid>
                </Grid>
                {/* bot detection result area */}
                {!directToGoogleLogin && (
                  <BotDetection
                    botCheckPassed={botCheckPassed}
                    setBotCheckPassed={setBotCheckPassed}
                  />
                )}
                {/* button area */}
                <Grid
                  item
                  sx={{
                    width: "100%",
                    height: "auto",
                    marginBottom: "24px",
                  }}>
                  {directToGoogleLogin && (
                    <>
                      {/* Google Button */}
                      <Grid
                        item
                        sx={{ width: "100%", height: "60px" }}
                        className="start-with-google">
                        <Button
                          color="primary"
                          fullWidth
                          onClick={handleGoogleSubmit}
                          sx={{
                            border: "2px solid #16124C",
                            borderRadius: "6px",
                            height: "100%",
                          }}>
                          {googleSubmitInProcess ? (
                            <CircularProgress size={17} color="inherit" />
                          ) : (
                            <>
                              <img
                                src="https://lh3.googleusercontent.com/COxitqgJr1sJnIDe8-jiKhxDx1FrYbtRHKJ9z_hELisAlapwE9LUPh6fcXIfb5vwpbMl4xl9H9TRFPc5NOO8Sb3VSgIBrfRYvW6cUA"
                                alt="google"
                                style={{
                                  height: "24px",
                                  marginRight: "10px",
                                }}
                              />
                              <Typography
                                sx={{
                                  color: "var(--Primary-500, #16124C)",
                                  fontFamily: "Lato",
                                  fontSize: 14,
                                  fontStyle: "normal",
                                  fontWeight: 700,
                                  lineHeight: "normal",
                                  textTransform: "none",
                                }}>
                                Continue with Google
                              </Typography>
                            </>
                          )}
                        </Button>
                      </Grid>
                    </>
                  )}
                  {/* Button */}
                  {!directToGoogleLogin && (
                    <Grid
                      item
                      sx={{
                        width: "100%",
                        height: "60px",
                      }}>
                      <LabelButton
                        type="primary"
                        variant="contained"
                        text={"Continue"}
                        disabled={!botCheckPassed}
                        function={handleContinue}
                        disableButtonStyle={{
                          border: "none",
                          background: "var(--Tertiary-100, #EBF8FF)",
                          color: "var(--Tertiary-500, #00A6FF)",
                          opacity: 0.5,
                        }}
                      />
                    </Grid>
                  )}
                </Grid>
                {/* switch to register option */}
                {!directToGoogleLogin && (
                  <Grid
                    item
                    sx={{
                      width: "100%",
                      height: "17px",
                    }}>
                    <Typography
                      sx={{
                        color: "var(--Primary-500, #16124C)",
                        fontFamily: "Lato",
                        fontSize: 14,
                        fontStyle: "normal",
                        fontWeight: 400,
                        lineHeight: "normal",
                      }}>
                      Don’t have an account?{" "}
                      <span
                        style={{
                          color: "var(--Tertiary-500, #00A6FF)",
                          cursor: "pointer",
                        }}
                        onClick={handleSwitchToRegister}>
                        Register here
                      </span>
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        {/* If the screen size is extra small, do not show the image */}
        <Grid
          item
          xs={6}
          className="register-page-image"
          sx={{
            height: "100%",
            display: { xs: "none", md: "block" },
          }}>
          <img
            src="/images/Product_registration-01.png"
            alt="register"
            className="registration-image"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              objectPosition: "center",
              borderRadius: "6px",
            }}
          />
        </Grid>
      </Grid>
    </Grid>
  );
}
